<template>
	<div class="modal fade show" tabindex="-1" role="dialog" style="display: block; background: rgba(0, 0, 0, 0.5);">
		<div class="modal-dialog modal-lg modal-fullscreen-sm-down" role="document">
			<div class="modal-content">
				<div class="modal-header bg-secondary text-white">
					<h5 class="modal-title">Nuova Elaborazione</h5>
					<button type="button" class="btn-close btn-close-white" @click="close()"></button>
				</div>

				<div class="modal-body">
					<!-- STEP 1: Selezione Utente e Upload -->
					<div v-if="step == 1">
						<div class="mb-3">
							<label class="form-label">Utente *</label>
							<select v-model="selectedUtente" class="form-control" required>
								<option value="">Seleziona...</option>
								<option v-for="u in $root.utentiAdminAttivi" :key="u.ID_UTENTE" :value="u.ID_UTENTE">
									{{ u.NOME }} {{ u.COGNOME }}
								</option>
							</select>
						</div>

						<div class="row mb-3">
							<div class="col-6">
								<label class="form-label">Mese di riferimento *</label>
								<select v-model="selectedMese" class="form-control" required>
									<option :value="null">Seleziona...</option>
									<option v-for="m in utils.arrayMesi" :key="m.ID_MESE" :value="m.ID_MESE">
										{{ m.DESCRIZIONE }}
									</option>
								</select>
							</div>
							<div class="col-6">
								<label class="form-label">Anno *</label>
								<select v-model="selectedAnno" class="form-control" required>
									<option :value="null">Seleziona...</option>
									<option v-for="a in arrayAnni" :key="a" :value="a">{{ a }}</option>
								</select>
							</div>
						</div>

						<div class="mb-3">
							<label class="form-label">File Excel (.xlsx) *</label>
							<p class="text-muted small mb-2">
								Servono almeno data, descrizione e importo: le colonne del file si abbinano dopo averlo scelto.<br/>
								Importo negativo = uscita (spesa), importo positivo = accredito
							</p>
							<input
								type="file"
								accept=".xlsx"
								@change="onFileSelect"
								class="form-control"
								required
							/>
						</div>

						<ExcelMapper
							v-if="selectedFile"
							:file="selectedFile"
							tipo-import="NOTE_SPESE"
							:campi="campiExcel"
							v-model="mapping"
							@valido="mappingValido = $event"
						/>

						<div v-if="message" class="alert" :class="messageClass" role="alert">
							{{ message }}
						</div>

						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" @click="close()">Annulla</button>
							<button
								type="button"
								class="btn btn-primary"
								@click="parseExcel()"
								:disabled="!selectedUtente || !selectedMese || !selectedAnno || !selectedFile || !mappingValido || loading"
							>
								<span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
								Avanti
							</button>
						</div>
					</div>

					<!-- STEP 2: Review Righe -->
					<div v-if="step == 2">
						<div v-if="dataLimite" class="alert alert-warning">
							<strong>Data limite:</strong> {{ formatData(dataLimite) }}<br/>
							Righe con data ≤ di questa data saranno escluse dall'importazione.
						</div>

						<div class="mb-3">
							<h6 class="mb-2">Righe Accettate ({{ arrayAccettate.length }})</h6>
							<div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
								<table class="table table-sm table-striped">
									<thead class="sticky-top bg-light">
										<tr>
											<th>Data</th>
											<th>Tipo</th>
											<th>Categoria</th>
											<th>Descrizione</th>
											<th style="width: 100px;">Importo</th>
											<th style="width: 60px;">Divisa</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(riga, idx) in arrayAccettate" :key="'acc-' + idx">
											<td>{{ riga.DATA }}</td>
											<td :class="riga.TIPO_MOVIMENTO == 0 ? 'text-danger' : 'text-success'">
												{{ riga.TIPO_MOVIMENTO == 0 ? 'Addebito' : 'Accredito' }}
											</td>
											<td>{{ riga.CATEGORIA }}</td>
											<td>{{ riga.DESCRIZIONE }}</td>
											<td class="text-end" :class="riga.TIPO_MOVIMENTO == 0 ? 'text-danger' : 'text-success'">
												{{ riga.TIPO_MOVIMENTO == 0 ? '-' : '+' }} € {{ parseFloat(riga.IMPORTO).toFixed(2) }}
											</td>
											<td>{{ riga.DIVISA }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<div v-if="arrayRifiutate.length > 0" class="mb-3">
							<h6 class="mb-2">Righe Rifiutate ({{ arrayRifiutate.length }}) — Non verranno importate</h6>
							<div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
								<table class="table table-sm table-striped bg-light">
									<thead class="sticky-top bg-secondary text-white">
										<tr>
											<th>Data</th>
											<th>Tipo</th>
											<th>Categoria</th>
											<th>Descrizione</th>
											<th style="width: 100px;">Importo</th>
											<th style="width: 60px;">Divisa</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(riga, idx) in arrayRifiutate" :key="'rif-' + idx">
											<td>{{ riga.DATA }}</td>
											<td :class="riga.TIPO_MOVIMENTO == 0 ? 'text-danger' : 'text-success'">
												{{ riga.TIPO_MOVIMENTO == 0 ? 'Addebito' : 'Accredito' }}
											</td>
											<td>{{ riga.CATEGORIA }}</td>
											<td>{{ riga.DESCRIZIONE }}</td>
											<td class="text-end" :class="riga.TIPO_MOVIMENTO == 0 ? 'text-danger' : 'text-success'">
												{{ riga.TIPO_MOVIMENTO == 0 ? '-' : '+' }} € {{ parseFloat(riga.IMPORTO).toFixed(2) }}
											</td>
											<td>{{ riga.DIVISA }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<div class="alert alert-info">
							<strong>Totale da importare:</strong> €{{ totalAccettate }}<br/>
							<strong>Righe:</strong> {{ arrayAccettate.length }}
						</div>

						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" @click="step = 1">Indietro</button>
							<button
								type="button"
								class="btn btn-primary"
								@click="step = 3"
								:disabled="arrayAccettate.length == 0"
							>
								Avanti
							</button>
						</div>
					</div>

					<!-- STEP 3: Conferma e Importa -->
					<div v-if="step == 3">
						<div class="alert alert-success">
							<i class="ph-check-circle me-2"></i>
							Conferma importazione di <strong>{{ arrayAccettate.length }} righe</strong> per un totale di <strong>€{{ totalAccettate }}</strong>
						</div>

						<p class="text-muted">
							Le note spese verranno caricate nel sistema. Potrai assegnare i progetti e caricare gli allegati successivamente.
						</p>

						<div v-if="message" class="alert" :class="messageClass" role="alert">
							{{ message }}
						</div>

						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" @click="step = 2" :disabled="loading">Indietro</button>
							<button
								v-if="$root.hasFunzionalita('NOTE_SPESE_IMPORT')"
								type="button"
								class="btn btn-primary"
								@click="confirmImport()"
								:disabled="loading"
							>
								<span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
								Importa
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ExcelMapper from '@/components/common/ExcelMapper.vue'

export default {
	name: 'ImportSpeseExcel',
	components: { ExcelMapper },
	props: {
		presetUtente: {
			type: [Number, String],
			default: null
		},
		presetData: {
			type: String,
			default: null
		}
	},
	data() {
		const presetParts = (this.presetData || '').split('-');
		return {
			step: 1,
			utils: global.utils,
			selectedUtente: this.presetUtente || '',
			selectedMese: presetParts.length == 3 ? parseInt(presetParts[1]) : null,
			selectedAnno: presetParts.length == 3 ? parseInt(presetParts[0]) : null,
			idElaborazioneCreata: null,
			selectedFile: null,
			mapping: {},
			mappingValido: false,
			campiExcel: [
				{ key: 'DataOperazione', label: 'Data operazione', required: true, tipo: 'data', alias: ['data', 'data contabile', 'data registrazione', 'data movimento'] },
				{ key: 'DataValuta', label: 'Data valuta', tipo: 'data', alias: ['valuta'] },
				{ key: 'Categoria', label: 'Categoria', alias: ['tipologia'] },
				{ key: 'Descrizione', label: 'Descrizione', required: true, alias: ['causale', 'dettagli', 'descrizione operazione', 'esercente'] },
				{ key: 'Importo', label: 'Importo', required: true, alias: ['ammontare', 'importo eur', 'totale'] },
				{ key: 'Divisa', label: 'Divisa', alias: ['currency', 'moneta'] },
			],
			arrayAccettate: [],
			arrayRifiutate: [],
			dataLimite: null,
			message: '',
			messageClass: '',
			loading: false
		};
	},
	computed: {
		arrayAnni() {
			const annoCorrente = new Date().getFullYear();
			const anni = [];
			for (let a = annoCorrente; a >= annoCorrente - 3; a--) {
				anni.push(a);
			}
			return anni;
		},
		selectedData() {
			if (!this.selectedMese || !this.selectedAnno) return '';
			return `${this.selectedAnno}-${String(this.selectedMese).padStart(2, '0')}-01`;
		},
		totalAccettate() {
			return this.arrayAccettate.reduce((sum, r) => {
				const v = parseFloat(r.IMPORTO || 0);
				return sum + (r.TIPO_MOVIMENTO == 0 ? -v : v);
			}, 0).toFixed(2);
		}
	},
	methods: {
		onFileSelect(e) {
			this.selectedFile = e.target.files[0];
			this.message = '';
		},
		parseExcel() {
			if (!this.selectedUtente || !this.selectedData || !this.selectedFile) {
				this.message = 'Seleziona utente, data e file';
				this.messageClass = 'alert-danger';
				return;
			}

			this.loading = true;
			this.message = '';

			utils.get('theb/elaborazione/verifica-esistente', { FK_ID_UTENTE: this.selectedUtente, DATA: this.selectedData }, (res) => {
				const dati = res.data || {};

				if (dati.trovata) {
					this.loading = false;
					const nomeUtente = this.getNomeUtenteSelezionato();
					const periodo = new Date(dati.elaborazione.DATA).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

					utils.alert.confirm(
						`Esiste già un'elaborazione per ${nomeUtente} riferita a ${periodo}, con ${dati.tot_movimenti} movimento/i collegato/i. ` +
						`Sostituendola, l'elaborazione precedente e le relative note spese verranno eliminate definitivamente. Vuoi procedere?`,
						() => {
							this.loading = true;
							utils.put('theb/elaborazione/sostituisci', {
								ID_ELABORAZIONE: dati.elaborazione.ID_ELABORAZIONE,
								FK_ID_UTENTE: this.selectedUtente,
								DATA: this.selectedData
							}, (resSost) => {
								if (resSost.data.esito != 0) {
									this.loading = false;
									this.message = resSost.data.messaggio || 'Errore nella sostituzione dell\'elaborazione';
									this.messageClass = 'alert-danger';
									return;
								}
								this.procediConUpload(resSost.data.id_inserito);
							});
						},
						this.$root,
						'Elaborazione già esistente',
						() => { this.loading = false; },
						'Sostituisci',
						'Annulla'
					);
					return;
				}

				utils.post('theb/elaborazione/insert', { FK_ID_UTENTE: this.selectedUtente, DATA: this.selectedData }, (resIns) => {
					if (resIns.data.esito != 0) {
						this.loading = false;
						this.message = resIns.data.messaggio || 'Errore nella creazione dell\'elaborazione';
						this.messageClass = 'alert-danger';
						return;
					}
					this.procediConUpload(resIns.data.id_inserito);
				});
			});
		},
		procediConUpload(idElaborazione) {
			this.idElaborazioneCreata = idElaborazione;

			const formData = new FormData();
			formData.append('file1', this.selectedFile);
			formData.append('args', JSON.stringify({
				modalita: 'THEB_uploadSpeseExcel',
				FK_ID_UTENTE: this.selectedUtente,
				FK_ID_ELABORAZIONE: this.idElaborazioneCreata,
				MAPPING: this.mapping
			}));

			utils.uploadAjax('core/basic/upload', formData, (uploadRes) => {
				this.loading = false;
				if (uploadRes.esito == 0) {
					this.arrayAccettate = uploadRes.data.accettate || [];
					this.arrayRifiutate = uploadRes.data.rifiutate || [];
					this.dataLimite = uploadRes.data.dataLimite || null;
					this.step = 2;
				} else {
					this.message = uploadRes.messaggio || 'Errore nel parse del file';
					this.messageClass = 'alert-danger';
				}
			});
		},
		getNomeUtenteSelezionato() {
			const u = (this.$root.arrayUtentiAdmin || []).find(x => x.ID_UTENTE == this.selectedUtente);
			return u ? `${u.NOME} ${u.COGNOME}` : 'questo utente';
		},
		confirmImport() {
			if (this.arrayAccettate.length == 0) {
				this.message = 'Nessuna riga da importare';
				this.messageClass = 'alert-danger';
				return;
			}

			this.loading = true;
			this.message = '';

			// Prepara i dati per l'insert massivo
			const arrayRighe = this.arrayAccettate.map(r => ({
				FK_ID_UTENTE: parseInt(this.selectedUtente),
				FK_ID_PROGETTO: null,
				DATA: r.DATA,
				IMPORTO: r.IMPORTO,
				DESCRIZIONE: r.DESCRIZIONE,
				FILE_ALLEGATO: null,
				FLG_ATTIVO: 1,
				DATA_ADD: new Date().toISOString().split('T')[0],
				FK_ID_CATEGORIA: r.FK_ID_CATEGORIA ?? null,
				DIVISA: r.DIVISA ?? null,
				DATA_VALUTA: r.DATA_VALUTA ?? null,
				TIPO_MOVIMENTO: r.TIPO_MOVIMENTO ?? 0,
				FK_ID_ELABORAZIONE: this.idElaborazioneCreata,
				FLG_FATTURA: null
			}));

			const payload = {
				array_spese: arrayRighe,
				FK_ID_UTENTE: this.selectedUtente
			};

			utils.post('theb/note_spese/import', payload, (res) => {
				this.loading = false;
				if (res.esito == 0) {
					utils.alert.success(`Importate ${res.data.count} righe`);
					this.$emit('imported', res.data.count);
					this.close();
				} else {
					this.message = res.messaggio || 'Errore nell\'importazione';
					this.messageClass = 'alert-danger';
				}
			});
		},
		close() {
			this.$emit('close');
		},
		formatData(data) {
			if (!data) return '';
			const d = new Date(data);
			return d.toLocaleDateString('it-IT');
		}
	}
};
</script>

<style scoped>
.modal {
	z-index: 1050;
}

.modal.show {
	background-color: rgba(0, 0, 0, 0.5);
}

.sticky-top {
	position: sticky;
	top: 0;
}

@media (max-width: 575.98px) {
	.modal-footer {
		flex-direction: column-reverse;
	}
	.modal-footer > * {
		width: 100%;
		margin-left: 0 !important;
		margin-right: 0 !important;
	}
}
</style>
